<template>

    <div v-if="searchable" class="select select-searchable" :class="classes" v-click-outside="close">
        <button type="button" class="select-trigger" :disabled="loading || disabled" @click="toggle">
            <span class="select-trigger-label" :class="{ 'is-placeholder': !selectedLabel }">{{ selectedLabel || placeholder }}</span>
            <span class="arrow">
                <v-loader v-if="loading" size="xs" />
                <i v-else class="fas fa-chevron-down ml-5"></i>
            </span>
        </button>
        <transition name="dropdown">
            <div v-show="open" class="select-menu">
                <div class="select-search">
                    <v-input
                        ref="search"
                        size="sm"
                        :model="query"
                        :placeholder="searchPlaceholder || `${$t('labels.search')}...`"
                        @input="query = $event"
                        @keydown.native.down.prevent="moveHighlight(1)"
                        @keydown.native.up.prevent="moveHighlight(-1)"
                        @keydown.native.enter.prevent="selectHighlighted"
                        @keydown.native.esc.prevent="close"
                    />
                </div>
                <ul ref="list" class="select-options">
                    <li v-if="loading" class="select-option disabled">{{ $t('labels.loading') }}...</li>
                    <li v-else-if="!filteredOptions.length" class="select-option disabled">{{ $t('labels.no_available_options') }}</li>
                    <li
                        v-for="(option, index) in filteredOptions"
                        :key="option.value"
                        class="select-option"
                        :class="{ 'highlighted': index === highlight }"
                        :title="option.label"
                        @click="selectOption(option)"
                        @mouseenter="highlight = index"
                    >
                        <span>{{ option.label }}</span>
                        <i v-if="option.value === val" class="fas fa-check" />
                    </li>
                </ul>
            </div>
        </transition>
    </div>

    <div v-else class="select" :class="classes">
        <select v-model="val" :aria-label="ariaLabel" :disabled="loading ? true : disabled">
            <option v-if="loading" value="">{{ $t('labels.loading') }}...</option>
            <slot></slot>
            <option v-for="option in resolvedOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
        </select>
        <div class="arrow">
            <v-loader v-if="loading" size="xs" />
            <i v-else class="fas fa-chevron-down ml-5"></i>
        </div>
    </div>

</template>

<style scoped lang="scss">

    .select {
        overflow: hidden;
        position: relative;
        .arrow {
            align-items: center;
            color: $color-text;
            display: flex;
            font-size: 11px;
            height: 50px;
            justify-content: center;
            position: absolute;
            pointer-events: none;
            place-items: center;
            right: 0;
            top: 0;
        }
    }

    .select > select {
        @include font-medium(14px);
        appearance: none;
        background: $color-white;
        border: 1px solid $color-rule;
        border-radius: 3px;
        box-sizing: border-box;
        color: $color-text;
        cursor: pointer;
        display: block;
        min-width: 90px;
        outline: none;
        text-overflow: ellipsis;
        transition: border-color .3s;
        width: 100%;
        &:hover, &:focus {
            border-color: $color-text;
            color: $color-text;
        }
    }

    .select {
        &.size-sm {
            select {
                font-size: 13px;
                height: 40px;
                padding: 0 40px 0 12px;
            }
            .arrow {
                height: 40px;
                width: 40px;
            }
        }
        &.size-md {
            select {
                font-size: 14px;
                height: 50px;
                padding: 0 50px 0 15px;
            }
            .arrow {
                height: 50px;
                width: 50px;
            }
        }
    }

    .select-searchable {
        overflow: visible;
    }

    .select-trigger {
        @include font-medium(14px);
        align-items: center;
        background: $color-white;
        border: 1px solid $color-rule;
        border-radius: 3px;
        box-sizing: border-box;
        color: $color-text;
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        min-width: 90px;
        outline: none;
        text-align: left;
        transition: border-color .3s;
        width: 100%;
        &:hover, &:focus {
            border-color: $color-text;
        }
        &:disabled {
            background: $color-bg;
            cursor: not-allowed;
        }
        .arrow {
            color: $color-text;
            flex-shrink: 0;
            font-size: 11px;
        }
    }

    .select-trigger-label {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        &.is-placeholder {
            color: $color-muted;
        }
    }

    .select-searchable {
        &.size-sm .select-trigger {
            font-size: 13px;
            height: 40px;
            padding: 0 12px;
        }
        &.size-md .select-trigger {
            font-size: 14px;
            height: 50px;
            padding: 0 15px;
        }
    }

    .select-menu {
        @include shad(2);
        background: $color-white;
        border: 1px solid $color-rule;
        border-radius: 5px;
        box-sizing: border-box;
        left: 0;
        margin: 5px 0 0 0;
        padding: 15px 0;
        position: absolute;
        right: 0;
        top: 100%;
        z-index: 100;
    }

    .select-search {
        padding: 0 15px 10px 15px;
    }

    .select-options {
        list-style: none;
        margin: 0;
        max-height: 240px;
        overflow-y: auto;
        padding: 0;
    }

    .select-option {
        @include font-medium(13px);
        align-items: center;
        color: $color-text;
        cursor: pointer;
        display: flex;
        height: 30px;
        justify-content: space-between;
        padding: 0 15px;
        transition: all .3s;
        user-select: none;
        white-space: nowrap;
        span {
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .fas {
            flex-shrink: 0;
            margin-left: 10px;
        }
        &:hover, &.highlighted {
            background-color: $color-highlight;
        }
        &.disabled {
            background-color: $color-white !important;
            color: $color-muted;
            cursor: default;
        }
    }

</style>

<script>

import BaseApi from '@api/BaseApi';
import ClickOutside from 'vue-click-outside';

export default {

    directives: {
        ClickOutside,
    },

    props: {
        ariaLabel: {
            type: String,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        model: {
            required: true,
        },
        options: {
            type: Array,
            default: () => [],
        },
        placeholder: {
            type: String,
            default: '',
        },
        searchable: {
            type: Boolean,
            default: false,
        },
        searchPlaceholder: {
            type: String,
            default: '',
        },
        size: {
            type: String,
            default: 'md',
        },
        subloadKey: {
            type: String,
        },
        subloadUrl: {
            type: String,
        },
    },

    data() {
        return {
            val: this.model !== null && this.model !== undefined ? this.model : '',
            loading: false,
            subloaded: [],
            open: false,
            query: '',
            highlight: 0,
        }
    },

    computed: {

        classes() {
            return {
                'size-xs': this.size === 'xs',
                'size-sm': this.size === 'sm',
                'size-md': this.size === 'md',
            };
        },

        resolvedOptions() {
            return this.subloadUrl ? this.subloaded : this.options;
        },

        filteredOptions() {
            const query = this.query.trim().toLowerCase();
            if (!query) {
                return this.resolvedOptions;
            }
            return this.resolvedOptions.filter(option => String(option.label).toLowerCase().includes(query));
        },

        selectedLabel() {
            const selected = this.resolvedOptions.find(option => option.value === this.val);
            return selected ? selected.label : '';
        },

    },

    mounted() {
        if (this.subloadUrl) {
            this.subloadOptions();
        }
    },

    watch: {

        model(val) {
            this.val = val;
        },

        val(val) {
            this.$emit('input', val);
        },

        query() {
            this.highlight = 0;
        },

    },

    methods: {

        toggle() {
            if (this.open) {
                this.close();
            } else {
                this.openMenu();
            }
        },

        openMenu() {
            this.open = true;
            this.query = '';
            this.highlight = this.filteredOptions.findIndex(option => option.value === this.val);
            if (this.highlight < 0) {
                this.highlight = 0;
            }
            this.$nextTick(() => {
                if (this.$refs.search) {
                    this.$refs.search.focus();
                }
            });
        },

        close() {
            this.open = false;
        },

        selectOption(option) {
            this.val = option.value;
            this.close();
        },

        selectHighlighted() {
            const option = this.filteredOptions[this.highlight];
            if (option) {
                this.selectOption(option);
            }
        },

        moveHighlight(step) {
            if (!this.filteredOptions.length) {
                return;
            }
            const count = this.filteredOptions.length;
            this.highlight = (this.highlight + step + count) % count;
            this.scrollToHighlight();
        },

        scrollToHighlight() {
            this.$nextTick(() => {
                const list = this.$refs.list;
                if (!list) {
                    return;
                }
                const item = list.children[this.highlight];
                if (item) {
                    item.scrollIntoView({ block: 'nearest' });
                }
            });
        },

        subloadOptions() {
            this.loading = true;
            BaseApi.get(this.subloadUrl)
                .then((response) => {
                    this.loading = false;
                    this.subloaded = response.data.data.map(i => {
                        return {
                            value: i.id,
                            label: i[this.subloadKey ?? 'name'],
                        };
                    });
                }, (error) => {
                    this.loading = false;
                    this.$store.commit('setAlertWithError', error);
                });
        },

    },

}

</script>
